"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { myPhotoPath } from "@/lib/lib";
import { cn } from "@/lib/utils";
import { Message } from "@ai-sdk/react";
import { AnimatePresence, motion } from "framer-motion";
import { BotIcon, Minimize2, Send, UserIcon, X } from "lucide-react";
import { useEffect, useRef } from "react";
import { FaRobot } from "react-icons/fa6";
import ReactMarkdown from "react-markdown";
import { AISocialLinks } from "../common/social-links";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { ScrollArea, ScrollBar } from "../ui/scroll-area";

interface ChatbotFullscreenModalProps {
  isOpen: boolean;
  onClose: () => void;
  messages: Message[];
  setMessages: (messages: Message[]) => void;
  input: string;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: (e: React.FormEvent) => void;
  status: string;
  userEmail: string | null;
  isOnline: boolean;
}

export function ChatbotFullscreenModal({
  isOpen,
  onClose,
  messages,
  setMessages,
  input,
  handleInputChange,
  handleSubmit,
  status,
  userEmail,
  isOnline,
}: ChatbotFullscreenModalProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscapeKey);
    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="fixed inset-4 bg-white dark:bg-gray-900 rounded-lg shadow-2xl border border-purple-500/20 flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-500 to-blue-600 dark:from-purple-600 dark:to-blue-700 text-white p-4 rounded-t-lg flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="relative">
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={myPhotoPath} className="rounded-full" />
                    <AvatarFallback>
                      <FaRobot className="h-6 w-6" />
                    </AvatarFallback>
                  </Avatar>
                  <div
                    className={cn(
                      "absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white",
                      status === "streaming"
                        ? "bg-yellow-500"
                        : status === "error"
                          ? "bg-red-500"
                          : "bg-green-500"
                    )}
                  />
                </div>
                <div>
                  <h2 className="text-xl font-semibold">Hamza AI Chatbot</h2>
                  <div className="flex items-center gap-2">
                    <div
                      className={`w-2 h-2 rounded-full ${isOnline ? "bg-green-400" : "bg-gray-400"}`}
                    />
                    <span className="text-sm opacity-90">
                      {isOnline ? "Online" : "Offline"} • {userEmail}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onClose}
                  className="text-white hover:bg-white/20"
                >
                  <Minimize2 className="h-5 w-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onClose}
                  className="text-white hover:bg-white/20"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>

            {/* Messages Area */}
            <div className="flex-1 flex flex-col min-h-0">
              {messages.length === 0 ? (
                <div className="flex-1 flex items-center justify-center text-center text-muted-foreground">
                  <div>
                    <FaRobot className="h-16 w-16 mx-auto mb-4 text-purple-500" />
                    {/* <h3 className="text-xl font-semibold mb-2">
                      Welcome to the chat!
                    </h3> */}
                    <p>
                      Welcome to the chat, {userEmail || "user"} ! Ask me about
                      this portfolio or anything else.
                    </p>
                  </div>
                </div>
              ) : (
                <ScrollArea className="flex-1 p-4">
                  <div className="space-y-4">
                    {messages.map((message) => (
                      <div
                        key={message.id}
                        className={cn(
                          "flex gap-3 max-w-[80%]",
                          message.role === "user"
                            ? "ml-auto flex-row-reverse"
                            : "mr-auto"
                        )}
                      >
                        <div className="flex-shrink-0">
                          {message.role === "user" ? (
                            <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
                              <UserIcon className="h-4 w-4 text-white" />
                            </div>
                          ) : (
                            <Avatar className="h-8 w-8">
                              <AvatarImage src={myPhotoPath} />
                              <AvatarFallback>
                                <BotIcon className="h-4 w-4" />
                              </AvatarFallback>
                            </Avatar>
                          )}
                        </div>
                        <div
                          className={cn(
                            "rounded-lg p-3 max-w-full",
                            message.role === "user"
                              ? "bg-purple-500 text-white"
                              : "bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                          )}
                        >
                          <ReactMarkdown
                            components={{
                              a: ({ node, ...props }) => (
                                <a
                                  {...props}
                                  className="text-blue-400 hover:underline"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                />
                              ),
                              p: ({ node, ...props }) => (
                                <p {...props} className="leading-relaxed" />
                              ),
                            }}
                          >
                            {message.content}
                          </ReactMarkdown>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                  <ScrollBar orientation="vertical" />
                </ScrollArea>
              )}
            </div>

            {/* Footer with Social Links and Input */}
            <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 rounded-b-lg">
              <div className="p-3 border-b border-gray-200 dark:border-gray-700">
                <AISocialLinks messages={messages} setMessages={setMessages} />
              </div>
              <form onSubmit={handleSubmit} className="p-4">
                <div className="flex gap-3 items-end">
                  <Input
                    ref={inputRef}
                    value={input}
                    onChange={handleInputChange}
                    placeholder="Type your message..."
                    disabled={status === "streaming"}
                    className="flex-1 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <Button
                    type="submit"
                    disabled={status === "streaming" || !input.trim()}
                    className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white transition-all duration-200 hover:scale-105"
                  >
                    <Send
                      className={cn(
                        "h-4 w-4 transition-transform",
                        status === "streaming" && "animate-pulse"
                      )}
                    />
                  </Button>
                </div>
              </form>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
